import axios from '../helpers/axios';
import qs from 'querystring';
import { logError } from '../services';
import {
  IDomainUserRes,
  IUser,
  IAllUser,
  IDropdownResponse,
  IUpdateUserRoles,
  IJobTitle,
  ICreateUser,
  IUserAppRoles,
  IAdminUser,
  IResponse,
  IUserWithAccess,
  IUpdateUser,
  IUnifiedUser,
  IUserAppRoleDescription,
  IUserByEmail,
  ISageEmployee,
  IAccessInformation
} from '../models';

const baseAPI = `/api/users`;

export const getMe = async (): Promise<IUser> => {
  try {
    const { data } = await axios.get('/api/me');
    return {
      ...data
      // testing checks here
      // placeOrder: null,
      // userRoles: [UserRoles.BLUserAdmin]
    };
  } catch (error) {
    logError(error, 'getMe failed');
    return Promise.reject(error);
  }
};

export const getDomainUsers = async (filters?: {
  name?: string;
  email?: string;
  accountNames?: string[];
  reportsTo?: string;
  roleId?: string | number;
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortDirection?: string;
  query?: string;
  territoryId?: string | number;
  excludeTerritoryId?: string | number;
}): Promise<IDomainUserRes> => {
  try {
    const { data } = await axios.get(baseAPI, {
      params: filters,
      paramsSerializer: params => qs.stringify(params)
    });
    return data;
  } catch (error) {
    logError(error, 'getDomainUsers failed');
    return Promise.reject(error);
  }
};

export const getDomainUsersWithAccess = async (filters?: {
  name?: string;
  email?: string;
  accountNames?: string[];
  reportsTo?: string;
  roleId?: string | number;
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortDirection?: string;
  query?: string;
  territoryId?: string | number;
}): Promise<IResponse<IUserWithAccess[]>> => {
  try {
    const { data } = await axios.get(`${baseAPI}/with-access`, {
      params: filters,
      paramsSerializer: params => qs.stringify(params)
    });
    return data;
  } catch (error) {
    logError(error, 'getDomainUsersWithAccess failed');
    return Promise.reject(error?.response?.data);
  }
};

export const resetTwoFactorAuth = async (accountName: string): Promise<any> => {
  try {
    const { data } = await axios.delete(`${baseAPI}/two-factor`, {
      params: {
        accountName
      }
    });
    return data;
  } catch (error) {
    logError(error, 'resetTwoFactorAuth failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createUserAgreement = async (post: {
  username: string;
  personId: number | string | null;
  isAccepted: boolean;
}): Promise<any> => {
  try {
    const { data } = await axios.post(`${baseAPI}/agreements`, post);
    return data;
  } catch (error) {
    logError({ error, payload: { post } }, 'createUserAgreement failed');
    return Promise.reject(error);
  }
};

export const getAllUsers = async (): Promise<IAllUser[]> => {
  try {
    const { data } = await axios.get(`${baseAPI}/all`);
    return data;
  } catch (error) {
    logError({ error }, 'getAllUsers failed');
    return Promise.reject(error);
  }
};

export const getAllAppRoles = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`${baseAPI}/app-roles`);
    return data;
  } catch (error) {
    logError({ error }, 'getAllAppRoles failed');
    return Promise.reject(error);
  }
};

export const getAllAppRolesDescriptions = async (): Promise<IUserAppRoleDescription[]> => {
  try {
    const { data } = await axios.get(`${baseAPI}/user-role-descriptions`);
    return data;
  } catch (error) {
    logError({ error }, 'getAllAppRolesDescriptions failed');
    return Promise.reject(error);
  }
};

export const getUserAppRoles = async (entraUserId: string): Promise<IUserAppRoles> => {
  try {
    const { data } = await axios.get(`${baseAPI}/${entraUserId}/app-roles`);
    return data;
  } catch (error) {
    logError({ error }, 'getUserAppRoles failed');
    return Promise.reject(error);
  }
};

export const updateUserRoles = async (roleInformation: IUpdateUserRoles): Promise<any> => {
  try {
    const { data } = await axios.put(`${baseAPI}/app-roles`, roleInformation);
    return data;
  } catch (error) {
    logError({ error, payload: { roleInformation } }, 'updateEntraUserRoles failed');
    return Promise.reject(error);
  }
};

export const getUserJobTitles = async (filters?: { isExternal?: string }): Promise<IJobTitle[]> => {
  try {
    const { data } = await axios.get(`${baseAPI}/job-titles`, {
      params: filters,
      paramsSerializer: params => qs.stringify(params)
    });
    return data;
  } catch (error) {
    logError(error, 'getUserJobTiles failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getAppRolesByJobTitle = async (jobTitle?: number): Promise<string[]> => {
  try {
    const { data } = await axios.get(`${baseAPI}/app-roles/default/${jobTitle}`);
    return data;
  } catch (error) {
    logError(error, 'getDefaultAppRolesByJobTitle failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createUser = async (post: ICreateUser): Promise<any> => {
  try {
    const { data } = await axios.post(`${baseAPI}`, post);
    return data;
  } catch (error) {
    logError({ error, payload: { post } }, 'createUser failed');
    return Promise.reject(error?.response?.data);
  }
};

export const updateUser = async (id: number, payload: IUpdateUser): Promise<any> => {
  try {
    const { data } = await axios.put(`${baseAPI}/${id}`, payload);
    return data;
  } catch (error) {
    logError({ error, payload: { payload } }, 'updateUser failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getUser = async (id: string): Promise<IAdminUser> => {
  try {
    const { data } = await axios.get(`${baseAPI}/${id}`);
    return data;
  } catch (error) {
    logError({ error }, 'getUser failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getSageEmployees = async (): Promise<ISageEmployee[]> => {
  try {
    const { data } = await axios.get(`${baseAPI}/sage-employees`);
    return data;
  } catch (error) {
    logError({ error }, 'getSageEmployees failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getUnifiedUsers = async (filters?: {
  search?: string;
  jobTitle?: string | number;
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortDirection?: string;
}): Promise<IResponse<IUnifiedUser[]>> => {
  try {
    const { data } = await axios.get(`${baseAPI}/unified`, {
      params: filters,
      paramsSerializer: params => qs.stringify(params)
    });
    return data;
  } catch (error) {
    logError(error, 'getUnifiedUsers failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getUserByEmail = async (email: string): Promise<IUserByEmail> => {
  try {
    const { data } = await axios.get(`${baseAPI}/by-email`, { params: { email } });
    return data;
  } catch (error) {
    logError({ error }, 'getUser failed');
    return Promise.reject(error?.response?.data);
  }
};

export const migrateUserToEntra = async (personId: number): Promise<boolean> => {
  try {
    const { data } = await axios.post(`${baseAPI}/person/${personId}/to-entra`);
    return data;
  } catch (error) {
    logError({ error, payload: { personId } }, 'migrateUserToEntra failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getAccessInformation = async (accountName: string): Promise<IAccessInformation> => {
  try {
    const { data } = await axios.get(`${baseAPI}/user-access`, {
      params: { userName: accountName }
    });
    return data;
  } catch (error) {
    logError(error, 'getAccessInformation failed');
    return Promise.reject(error);
  }
};
